<template>
  <div id="credit-return-availability" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading Credit Return Availability rules..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-dialog
        v-if="editDialog"
        v-model="editDialog"
        max-width="750px"
        persistent
        @click:outside="resetEdition"
        @keydown.esc="resetEdition"
      >
        <i-edit-credit-return-availability
          :credit-return-availability-rule="editableRule"
          @cancel="resetEdition"
          @save="reload"
        ></i-edit-credit-return-availability>
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          action-text="Delete"
          :submitting="deletingRule || editingRule"
          :text="confirmationDialogText"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template>
          <v-row>
            <v-col cols="12" md="4" lg="3">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="editRule()"
              >
                Add Credit Return
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/credit-returns"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" md="3"> </v-col>
            <v-spacer></v-spacer>
            <v-col cols="6">
              <i-local-filter
                :filterPlaceholder="'Search by Target Type or Target(s) or Geography'"
                @filter="localFilter"
              />
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template
                v-if="
                  !loading && filteredCreditReturnAvailabilityRules.length === 0
                "
              >
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <v-col
                    v-for="item in filteredCreditReturnAvailabilityRules"
                    :key="item.id"
                    class="mt-0 pa-0"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.targetStores"
                            small
                            color="secondary"
                            >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                          >
                          <v-chip
                            v-else-if="item.targetBannerGroups"
                            small
                            color="accent"
                            >BANNER</v-chip
                          >
                          <v-chip
                            v-else-if="item.targetMultiStoreOperators"
                            small
                            color="primary"
                            >MSO</v-chip
                          >
                          <v-chip v-else small color="light-green accent-2"
                            >ALL</v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[1].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStores">
                            <span
                              v-for="item in item.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetBannerGroups">
                            <span
                              v-for="item in item.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetMultiStoreOperators">
                            <span
                              v-for="item in item.targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStoreDeliveryAddressStates">
                            <span
                              v-for="item in item.targetStoreDeliveryAddressStates"
                              :key="item.state"
                            >
                              {{ item.state }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetGeography === 'STORE'">{{
                            isILR ? 'Customer' : 'Retailer'
                          }}</span>
                          <span v-else>{{
                            $filters.sentenceCase(item.targetGeography)
                          }}</span>
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="12" class="py-1">
                          <v-btn
                            class="i-btn-actions-blue g-full-width"
                            @click="editRule(item)"
                          >
                            <span>Edit Credit Returns Rule</span>
                            <v-icon small class="ml-1"
                              >mdi-circle-edit-outline</v-icon
                            >
                          </v-btn>
                        </v-col>
                        <v-col cols="12" class="py-1">
                          <v-btn
                            class="i-btn-actions-red g-full-width"
                            @click="deleteCreditReturnAvailabilityRule(item.id)"
                          >
                            <span>Delete Credit Returns Rule</span>
                            <v-icon small class="ml-1"
                              >mdi-delete-outline</v-icon
                            >
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1"
                    :headers="headers"
                    :items="filteredCreditReturnAvailabilityRules"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.targetStores"
                        small
                        color="secondary"
                        >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetBannerGroups"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetMultiStoreOperators"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip v-else small color="light-green accent-2"
                        >ALL</v-chip
                      >
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span v-if="item.targetStores">
                        <span
                          v-for="item in item.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.storeName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetBannerGroups">
                        <span
                          v-for="item in item.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.bannerGroupName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetMultiStoreOperators">
                        <span
                          v-for="item in item.targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.multiStoreOperatorName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template #[`item.targetGeography`]="{ item }">
                      <span v-if="item.targetStoreDeliveryAddressStates">
                        <span
                          v-for="item in item.targetStoreDeliveryAddressStates"
                          :key="item.state"
                        >
                          {{ item.state }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetGeography === 'STORE'">{{
                        isILR ? 'Customer' : 'Retailer'
                      }}</span>
                      <span v-else>{{
                        $filters.sentenceCase(item.targetGeography)
                      }}</span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="editRule(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="deleteCreditReturnAvailabilityRule(item)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchCreditReturnAvailabilityRules,
  deleteCreditReturnAvailabilityRule
} from '@/services/creditReturnAvailability'
import FiltersHelper from '@/modules/dataMaintenance/helper/FiltersHelper'
import iEditCreditReturnAvailability from '../components/EditCreditReturnAvailability.vue'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

export default {
  name: 'credit-return-availability',

  components: {
    iEmptyResults,
    iInstructions,
    iEditCreditReturnAvailability,
    iConfirmationDialog,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    confirmationDialog: false,
    confirmationDialogText: '',
    creditReturnAvailabilityRules: [],
    deletingRule: false,
    editDialog: false,
    editableRule: {},
    editingRule: false,
    selectedRule: null,
    filterText: '',
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: 180,
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        align: 'center',
        sortable: false
      },
      {
        text: 'GEOGRAPHY',
        width: 180,
        value: 'targetGeography',
        align: 'center'
      },
      {
        text: 'ACTIONS',
        width: 180,
        value: 'actions',
        sortable: false,
        align: 'center'
      }
    ],
    loading: false
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('profile', ['creditReturnEnabled']),
    ...mapGetters('tenant', [
      'featureCreditReturnAutoEnrolmentEnabled',
      'isILR'
    ]),

    emptyFilteredResultsText() {
      return `No Credit Returns rules found`
    },

    filteredCreditReturnAvailabilityRules() {
      const searchTextLowerCase = this.filterText.toLowerCase()

      return this.creditReturnAvailabilityRules.filter((rule) => {
        return (
          rule.targetGeography.toLowerCase().includes(searchTextLowerCase) ||
          rule.targetStoreDeliveryAddressStates?.filter((s) =>
            s.state.toLowerCase().includes(searchTextLowerCase)
          ).length > 0 ||
          FiltersHelper.filterByTargetType(rule, searchTextLowerCase) ||
          FiltersHelper.filterByTargets(rule, searchTextLowerCase)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    // ***
    // Functions related to handling actions
    // ***

    deleteCreditReturnAvailabilityRule(item) {
      this.selectedRule = item
      this.confirmationDialogText =
        'Confirm deletion of this Credit Returns rule'
      this.confirmationDialog = true
    },

    confirmDeletion() {
      this.deleteRule(this.selectedRule.id)
    },

    reload() {
      this.getRules()
      this.resetEdition()
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.confirmationDialogText = ''
      this.selectedRule = null
    },

    resetEdition() {
      this.editableRule = {}
      this.editDialog = false
    },

    // ***
    // Functions related to creditReturnAvailabilityRules data
    // ***

    editRule(rule = {}) {
      this.editableRule = _.cloneDeep(rule)
      this.editDialog = true
    },

    deleteRule(ruleId) {
      this.deletingRule = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting Credit Returns rule...'
      })

      deleteCreditReturnAvailabilityRule(this.selectedSupplierId, ruleId)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Credit Returns rule has been deleted!'
          })
          this.getRules()
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.deletingRule = false
          this.resetDeletion()
        })
    },

    getRules() {
      this.loading = true
      fetchCreditReturnAvailabilityRules(this.selectedSupplierId)
        .then((response) => {
          this.creditReturnAvailabilityRules = response
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Credit Returns rules could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    // Note: this is a simple guard to ensure that users can't browse to this page directly if the appropriate options aren't enabled.
    // It is already hidden on the DataMaintenance.vue landing page, so if changing the behaviour there you will need to change it here too!
    if (
      !this.creditReturnEnabled ||
      this.featureCreditReturnAutoEnrolmentEnabled
    )
      this.$router.push({ name: 'dashboard' })

    this.getRules()
  }
}
</script>

<style lang="scss" scoped>
// #creditReturnAvailabilityRules {}
</style>
